import type { User } from '@clockwise/clients/users';

// The display-name rule lives in `@clockwise/shared` so the API's
// server-side label enrichment and the web UI apply the identical rule
// ("firstName lastName" → email; legal name, not preferredName). Re-exported
// here so the existing `@/lib/user-display` import sites keep working unchanged.
export { userDisplayName } from '@clockwise/shared';

/**
 * Derived employment status — read-only in the UI. Returns 'Active' iff
 * the user record is marked `active` AND today falls within
 * [startDate, terminatedDate]. Either bound being null is treated as
 * "unbounded on that side".
 *
 * `asOf` defaults to today in the local timezone, expressed as
 * YYYY-MM-DD. The comparison is lexicographic on ISO strings, which is
 * safe because `date` columns are stored in the same shape.
 */
export function userStatus(
  u: Pick<User, 'active' | 'startDate' | 'terminatedDate'>,
  asOf: string = todayIso(),
): 'Active' | 'Inactive' {
  if (!u.active) return 'Inactive';
  if (u.startDate && asOf < u.startDate) return 'Inactive';
  if (u.terminatedDate && asOf > u.terminatedDate) return 'Inactive';
  return 'Active';
}

/**
 * YYYY-MM-DD for "today" — defined in UTC to match the server. The API's
 * own todayIso (apps/api/src/core/users/service.ts) uses
 * `new Date().toISOString().slice(0, 10)`; keeping the FE on the same
 * basis means userStatus, the cost-as-of header, and a server-side
 * export run within seconds of each other all agree on which calendar
 * day is "today" — important for the local-midnight window where a
 * local-tz today would disagree with the server.
 */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The list of IANA time zones the runtime can resolve. Pulled from
 * `Intl.supportedValuesOf('timeZone')` which is the canonical source —
 * stays in sync with whatever zones the browser's Intl implementation
 * actually understands. Falls back to a short curated list on engines
 * where the API isn't available (older Node test runs).
 */
export function listIanaTimezones(): readonly string[] {
  // `Intl.supportedValuesOf` landed in Node 18 / all current browsers.
  const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
  if (typeof fn === 'function') {
    try {
      return fn('timeZone');
    } catch {
      // Some polyfills throw on unknown keys; fall through.
    }
  }
  return ['UTC', 'America/Los_Angeles', 'America/New_York', 'Asia/Kolkata', 'Europe/London'];
}
